'use client';

import {
	BASE_PRECISION_EXP,
	BigNum,
	QUOTE_PRECISION_EXP,
	SpotBalanceType,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import { useAllWalletBalances } from './query/walletBalances/useWalletBalance';
import useAccountSpotBalances from './useAccountBankBalances';
import { useMemo, useRef } from 'react';
import { useSpotMarkets } from './spotMarkets/useSpotMarkets';
import { NEW_MARKET_CUT_OFF_TIME } from '../constants/constants';
import { useMappedIfStakeBalances } from './insuranceFund/useMappedIfStakeBalances';
import { getCleanedMarketSymbol } from 'src/utils/market';

export type BankRenderingInfo = {
	base: BigNum;
	quote: BigNum;
	baseValue: number;
	quoteValue: number;
	market: SpotMarketConfig;
	highlighted?: boolean;
	walletBaseValue?: number;
	accountBaseValue?: number;
	marketIsNew: boolean;
};

/**
 * Returns a list of banks/spot markets, sorted by configurable options. The purpose is to easily pull the list of assets to display in the UI, e.g. in the Deposit form or the Asset Balances table. Encapsulates any logic like sorting the banks by user's balance, or highlighting particular assets.
 *
 * Using DEFAULT will
 * @returns
 */
const useBanksToRender = (props: {
	banks?: SpotMarketConfig[];
	poolId?: number;
	sortBy: 'default' | 'accountBalance' | 'walletBalance' | 'ifStakeBalance';
	userKey: string | undefined;
	opts?: {
		highlightType?: 'forceDespiteBalance' | 'higherPriorityIfZeroBalance';
		highlightBanks?: SpotMarketConfig[]; // Of any bank balances which are 0, highlighted banks will be sorted first
		prioritySort?: Record<SpotMarketConfig['marketIndex'], number>; // Of any bank balances which are 0, banks to sort first but won't be highlighted
	};
	filterOutMarketIndices?: number[];
}): BankRenderingInfo[] => {
	// TODO :: I think could memoize this hook better so it doesn't recalculate so much stuff each time it renders

	const spotMarkets = useSpotMarkets();

	const BanksToUse = props.banks
		? props.banks
		: spotMarkets.filter(
				(market) =>
					market.poolId === (props.poolId || 0) &&
					!props.filterOutMarketIndices?.includes(market.marketIndex)
			);

	const banksToHighlightSymbol = (
		props.opts?.highlightBanks
			? props.opts?.highlightBanks?.map((bank) => bank?.symbol)
			: []
	).filter((a) => !!a);

	// # Default mapped bank info
	const allBanksDefault: BankRenderingInfo[] = BanksToUse.map((bank) => ({
		base: BigNum.zero(BASE_PRECISION_EXP),
		quote: BigNum.zero(QUOTE_PRECISION_EXP),
		baseValue: 0,
		quoteValue: 0,
		market: bank,
		marketIsNew: (bank.launchTs ?? 0) > NEW_MARKET_CUT_OFF_TIME,
	}));

	// # Map user account balances to bank info
	const mappedAccountBalances = useAccountSpotBalances(
		props.userKey,
		SpotBalanceType.DEPOSIT
	).map((balance) => {
		const isBorrow = ENUM_UTILS.match(
			balance.balanceType,
			SpotBalanceType.BORROW
		);

		return {
			base: isBorrow ? BigNum.zero(BASE_PRECISION_EXP) : balance.balance,
			quote: isBorrow ? BigNum.zero(QUOTE_PRECISION_EXP) : balance.quoteValue,
			baseValue: isBorrow ? 0 : balance.balance.toNum(),
			quoteValue: isBorrow ? 0 : balance.quoteValue.toNum(),
			market: balance.asset,
		};
	});

	// # Map wallet balances to bank info
	const allWalletBalances = useAllWalletBalances();
	const mappedWalletBalances: BankRenderingInfo[] = BanksToUse.map((bank) => {
		const cleanedSymbol = getCleanedMarketSymbol(bank.symbol);
		const balances = allWalletBalances.get(cleanedSymbol);

		return {
			base: balances?.base ?? BigNum.zero(BASE_PRECISION_EXP),
			quote: balances?.quote ?? BigNum.zero(QUOTE_PRECISION_EXP),
			baseValue: balances?.base.toNum() ?? 0,
			quoteValue: balances?.quote.toNum() ?? 0,
			market: bank,
			marketIsNew: (bank.launchTs ?? 0) > NEW_MARKET_CUT_OFF_TIME,
		};
	});

	// # Map IF Stake balances to bank info
	const mappedIfStakeBalances = useMappedIfStakeBalances();

	// # Map all banks, merging with balances based on the sort option
	const mappedRenderingInfo = allBanksDefault.map((bank) => {
		const accountBalance = mappedAccountBalances.find(
			(balance) => balance.market.marketIndex === bank.market.marketIndex
		);
		const walletBalance = mappedWalletBalances.find(
			(balance) => balance.market.symbol === bank.market.symbol
		);
		const ifStakeBalance = mappedIfStakeBalances.find(
			(balance) => balance.market?.marketIndex === bank.market.marketIndex
		);

		let balanceToUse:
			| BankRenderingInfo
			| Partial<BankRenderingInfo>
			| undefined;

		switch (props.sortBy) {
			case 'accountBalance':
				balanceToUse = accountBalance;
				break;
			case 'walletBalance':
				balanceToUse = walletBalance;
				break;
			case 'ifStakeBalance':
				balanceToUse = ifStakeBalance;
				break;
			case 'default':
				balanceToUse = bank;
				break;
			default: {
				const exhaustiveCheck: never = props.sortBy;
				throw new Error(
					`Unhandled sortBy option in useBanksToRender: ${exhaustiveCheck}`
				);
			}
		}

		return {
			...bank,
			base: balanceToUse?.base ?? BigNum.zero(BASE_PRECISION_EXP),
			quote: balanceToUse?.quote ?? BigNum.zero(QUOTE_PRECISION_EXP),
			baseValue: balanceToUse?.baseValue ?? 0,
			quoteValue: balanceToUse?.quoteValue ?? 0,
			walletBaseValue: walletBalance?.baseValue ?? 0,
			accountBaseValue: accountBalance?.baseValue ?? 0,
		};
	});

	const highlightType =
		props.opts?.highlightType ?? 'higherPriorityIfZeroBalance';

	const sortedRenderingInfo = mappedRenderingInfo.sort((a, b) => {
		const highlightA = banksToHighlightSymbol.includes(a.market.symbol);
		const highlightB = banksToHighlightSymbol.includes(b.market.symbol);

		const bothBalancesZero = a.quoteValue === 0 && b.quoteValue === 0;
		const balanceSortScore = b.quoteValue - a.quoteValue;
		const highlightSortScore =
			highlightA && !highlightB ? -1 : !highlightA && highlightB ? 1 : 0;

		const priorityA =
			props.opts?.prioritySort?.[a.market.marketIndex] ??
			Number.MAX_SAFE_INTEGER;
		const priorityB =
			props.opts?.prioritySort?.[b.market.marketIndex] ??
			Number.MAX_SAFE_INTEGER;
		const prioritySortScore = priorityA - priorityB;

		if (highlightType === 'higherPriorityIfZeroBalance') {
			if (bothBalancesZero) {
				return highlightSortScore || prioritySortScore;
			}
			return balanceSortScore;
		}

		if (highlightType === 'forceDespiteBalance') {
			if (highlightSortScore !== 0) {
				return highlightSortScore;
			}
			return balanceSortScore || prioritySortScore;
		}

		return 0;
	});

	const finalResult = sortedRenderingInfo.map((bank) => ({
		...bank,
		highlighted: banksToHighlightSymbol.includes(bank.market.symbol),
	}));

	const prevResultRef = useRef<BankRenderingInfo[]>([]);

	return useMemo(() => {
		// Deep comparison to check if the result has actually changed
		const hasChanged =
			!prevResultRef.current ||
			prevResultRef.current.length !== finalResult.length ||
			prevResultRef.current.some((prev, index) => {
				const current = finalResult[index];
				return (
					prev.base.toString() !== current.base.toString() ||
					prev.quote.toString() !== current.quote.toString() ||
					prev.baseValue !== current.baseValue ||
					prev.quoteValue !== current.quoteValue ||
					prev.market.marketIndex !== current.market.marketIndex ||
					prev.highlighted !== current.highlighted ||
					prev.walletBaseValue !== current.walletBaseValue ||
					prev.accountBaseValue !== current.accountBaseValue ||
					prev.marketIsNew !== current.marketIsNew
				);
			});

		if (hasChanged) {
			prevResultRef.current = finalResult;
		}

		return prevResultRef.current || finalResult;
	}, [finalResult]);
};

export default useBanksToRender;
